import { useEffect, useState } from "react";
import { useAuthHeader, useAuthUser } from "../../../Services/Auth/sessionAuth";
import { Button, Form, Space, Spin, Tabs, Tag, Tooltip } from 'antd';
import Utilisateur from "../../../Models/Utilisateur";
import type { TabsProps } from 'antd';
import { UserOutlined, EyeOutlined } from '@ant-design/icons';
import SweetAlert from "sweetalert2";
import { Crypter } from "../../../Functions/Security/Crypter";
import Data from '../../../Data/Data.json';
import axios from "axios";
import Tab1 from "./Tab1";
import { Typography, IconButton, Stack, Grid, Box } from "@mui/material";
import { normalizeKeysToUppercase } from "../../../utilitaires/globalFunction";
import Params from "../../../Types/ParamsPicklist";
import dayjs from 'dayjs';
import { ActifDocumentType, extractActifDocumentRows, getActifDocumentLabel, loadActifDocumentTypes, mapActifDocumentsToPreview, toDocumentDataUri } from '../../../Services/actifDocumentTypes';
import AssetDocumentPreviewModal from '../../AssetDocumentPreviewModal';

const buildDocuments = (row: any, definitions: ActifDocumentType[]) => {
    // Si on a des documents réels, les utiliser
    const documents = row.Documents || row.DOCUMENTS || row.documents || [];
    if (documents.length > 0) {
        return documents.map((document: any) => ({
            label: getActifDocumentLabel(document, definitions),
            base64: toDocumentDataUri(document)
        })).filter((document: any) => document.base64);
    }
    
    // Sinon, créer des entrées factices basées sur NbDocuments pour l'affichage du compteur
    const nbDocuments = row.NBDOCUMENTS || row.NbDocuments || 0;
    if (nbDocuments > 0) {
        return Array.from({ length: nbDocuments }, (_, index) => ({
            label: `Document ${index + 1}`,
            base64: null // Pas de contenu pour les documents factices
        }));
    }
    
    return [];
};

const CategorieActifPage = () => {
    const auth = useAuthUser();
    const Perss: Utilisateur = JSON.parse(JSON.stringify(auth()));
    const authHeader = useAuthHeader();
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [Consultactv, setConsulte] = useState(false);
    const [Ajoutactv, setAjoute] = useState(false);
    const [Modifactv, setModife] = useState(true);
    const [Enrege, setEnrege] = useState(true);
    const [Annulactv, setAnnule] = useState(true);
    const [FormAct, setFormactve] = useState(true);
    const [EtatG, setEtatG] = useState(false);
    const [sbmit, setSbmit] = useState("");

    const [listFinancements, setListFinancements] = useState<any[]>([]);
    const [documentDefinitions, setDocumentDefinitions] = useState<ActifDocumentType[]>([]);
    const [glActifsValides, setglActifsValides] = useState<any[]>([]);
    const [usersMap, setUsersMap] = useState<Record<string, string>>({});

    const [docModal, setDocModal] = useState<{ open: boolean; docs: { label: string; base64: string | null }[]; activeIndex: number | null }>({
        open: false, docs: [], activeIndex: null
    });

    // Fonction pour charger les documents d'un actif et ouvrir la modal
    const openDocsModalWithLoad = async (row: any) => {
        // Si les documents sont déjà chargés avec leur contenu, les utiliser directement
        const rowDocuments = row.Documents || row.DOCUMENTS || row.documents || [];
        if (rowDocuments.length > 0) {
            const docs = buildDocuments(row, documentDefinitions);
            if (docs.length > 0) {
                setDocModal({ open: true, docs, activeIndex: docs.length === 1 ? 0 : null });
                return;
            }
        }
        // Charger les documents depuis l'API (GetActifWithDocuments si dispo, sinon GetActifsByValidation)
        try {
            setLoading(true);
            const getActifWithDocuments = (Data.ApiUrl as any).GetActifWithDocuments;
            const useNewEndpoint = !!getActifWithDocuments;
            const headers: any = {
                'Authorization': authHeader(),
                'CypherCodeActif': Crypter.encrypt(row.CODEACTIF)
            };
            if (!useNewEndpoint) {
                headers['CypherStatutValidation'] = Crypter.encrypt(row.STATUTVALIDATION || 'VALIDE');
            }
            const response = await axios.get(useNewEndpoint ? getActifWithDocuments : Data.ApiUrl.GetActifsByValidation, { headers });
            const res = JSON.parse(Crypter.decrypt(response.data)).Value;
            if (res.StatutCode === 200) {
                const docRows = extractActifDocumentRows(res.Result, row.CODEACTIF);
                const docs = mapActifDocumentsToPreview(docRows, documentDefinitions);
                setDocModal({ open: true, docs, activeIndex: docs.length === 1 ? 0 : null });
            } else {
                setDocModal({ open: true, docs: [], activeIndex: null });
            }
        } catch (error) {
            console.error('Erreur chargement documents:', error);
            setDocModal({ open: true, docs: [], activeIndex: null });
        } finally {
            setLoading(false);
        }
    };

    const openDocsModal = (docs: { label: string; base64: string | null }[]) => setDocModal({ open: true, docs, activeIndex: docs.length === 1 ? 0 : null });
    const closeDocsModal = () => setDocModal({ open: false, docs: [], activeIndex: null });

    const loadDynamicList = (params: Params, setter: (data: any[]) => void) => {
        const config = {
            method: 'get',
            url: Data.ApiUrl.AnySearch,
            headers: { 'Content-Type': 'application/json', 'Authorization': authHeader(), 'CypherSearch': Crypter.encrypt(JSON.stringify(params)) }
        };
        axios(config).then((res) => {
            const response = JSON.parse(Crypter.decrypt(res.data)).Value;
            if (response.StatutCode === 200) {
                const table = response.Result.Table || response.Result || [];
                setter(table.map((item: any) => ({ CODE: item[params.Colonne1], LIBELLE: item[params.Colonne2] })));
            }
        }).catch(err => console.error("Erreur chargement liste:", err));
    };

    useEffect(() => {
        loadDynamicList({ Table: "TypeFinancement", Colonne1: "CodeFinancement", Colonne2: "Libelle", Choix: 22 }, setListFinancements);
        loadActifDocumentTypes(authHeader()).then(setDocumentDefinitions).catch(() => setDocumentDefinitions([]));
    }, []);

    useEffect(() => {
        const codes = new Set<string>();
        glActifsValides.forEach((row: any) => {
            if (row.ENREGISTREPAR) codes.add(row.ENREGISTREPAR);
            if (row.VALIDEPAR) codes.add(row.VALIDEPAR);
        });
        const toFetch = Array.from(codes).filter(c => c && !usersMap[c]);
        if (toFetch.length === 0) return;

        Promise.all(toFetch.map(async (coduti) => {
            try {
                const response = await axios.request({
                    method: 'get',
                    url: Data.ApiUrl.AfficheUser,
                    headers: {
                        'CypherUserCode': Crypter.encrypt(coduti),
                        'Authorization': authHeader(),
                        'Content-Type': 'application/json'
                    }
                });
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                const user = res?.Result?.Table?.[0];
                const name = user?.NOMUTI || user?.ANOMUTI || user?.nomuti || user?.anomuti || user?.lib_CODUTI || user?.LIB_CODUTI || '';
                return [coduti, name] as const;
            } catch {
                return [coduti, ''] as const;
            }
        })).then(entries => {
            setUsersMap(prev => {
                const next = { ...prev };
                entries.forEach(([coduti, name]) => { next[coduti] = name || coduti; });
                return next;
            });
        });
    }, [glActifsValides, authHeader, usersMap]);

    // --- CONSULTATION : catégorie + UNIQUEMENT ses actifs VALIDE ---
    // --- PHASE 1 : CONSULTATION (Version Finale Corrigée) ---
    function HandleConsult() {
        const code = form.getFieldValue("CODECATEGORIE");
        if (!code) {
            SweetAlert.fire("Alerte", "Veuillez choisir un code catégorie.", "warning");
            return;
        }

        setLoading(true);

        // ÉTAPE 1 : Récupération des informations de la Catégorie
        axios.get(Data.ApiUrl.GetByCodeCategorie, {
            headers: { 
                'Authorization': authHeader(), 
                'CypherCodeCategorie': Crypter.encrypt(code) 
            }
        }).then((response) => {
            const res = JSON.parse(Crypter.decrypt(response.data)).Value;

            if (res.StatutCode === 200) {
                const d = res.Result; 

                // Remplissage du formulaire Maître (Catégorie)
                form.setFieldsValue({
                    CODECATEGORIE: d.CodeCategorie,
                    LIBELLE: d.Libelle,
                    FAMILLE: d.CodeFamille,
                    lib_FAMILLE: d.LibelleFamille,
                    FINANCEMENT_ELIGIBLES: d.Financements,
                    REGLESUSAGE: d.ReglesUsage,
                    ESTLICITE: d.EstLicite,
                    ESTACTIF: d.EstActif
                });

                // ÉTAPE 2 : Appel à l'API spécialisée pour les actifs VALIDÉS
                const paramsValidation = Crypter.encrypt("VALIDE");
                
                axios.get(Data.ApiUrl.GetActifsByValidation, {
                    headers: { 
                        'Authorization': authHeader(), 
                        'CypherStatutValidation': paramsValidation
                    }
                }).then((respVal) => {
                    const resVal = JSON.parse(Crypter.decrypt(respVal.data)).Value;
                    
                    if (resVal.StatutCode === 200) {
                        // Extraction sécurisée du tableau (Table ou Result direct)
                        const rawData = resVal.Result?.Table || resVal.Result || [];
                        const allValides = normalizeKeysToUppercase(Array.isArray(rawData) ? rawData : [rawData]);
                        
                        // Filtrage : On ne garde que les actifs rattachés à cette catégorie
                        const actifsDeCetteCategorie = allValides.filter((a: any) => a.CODECATEGORIE === d.CodeCategorie);
                        
                        setglActifsValides(actifsDeCetteCategorie);
                    }
                    setLoading(false); // On arrête le loading après le deuxième appel
                });//.catch(() => setLoading(false));

                // Gestion des états des boutons
                setModife(false);
                setAnnule(false);
                setConsulte(true);
                setFormactve(true);

            } else {
                setLoading(false);
                SweetAlert.fire({ title: res.Message, text: res.Result, icon: "warning" });
            }
        }).catch((err) => {
            setLoading(false);
            console.log("Structure de l'erreur :", err); 
            SweetAlert.fire("Erreur", "Détail technique : " + err.message, "error");
        });
    }

    function HandleAjout() {
        form.resetFields();
        form.setFieldValue('ESTLICITE', true);
        setglActifsValides([]);
        setConsulte(true);
        setAjoute(true);
        setEnrege(false);
        setAnnule(false);
        setEtatG(true);
        setFormactve(false);
        setSbmit('add');
    }

    function HandleModif() {
        setEnrege(false);
        setModife(true);
        setEtatG(true);
        setFormactve(false);
        setSbmit('modif');
    }

    function HandleAnnul() {
        setEtatG(false);
        setConsulte(false);
        setAjoute(false);
        setModife(true);
        setEnrege(true);
        setAnnule(true);
        form.resetFields();
        form.setFieldValue('ESTLICITE', true);
        setFormactve(true);
        setglActifsValides([]);
        setSbmit('');
    }

    // --- PHASE 4 : ENGAGEMENT (Catégorie + Éligibilité en une seule transaction) ---
    const onFinish = () => {
        const values = form.getFieldsValue(true);
        const isAdd = sbmit === "add";
        
        // On utilise l'endpoint unifié "ManageFinancements" envoyé par le CTO
        // Cet endpoint gère la création/mise à jour de la catégorie et ses liens produits
        const url = Data.ApiUrl.ManageFinancements; 

        // Construction du payload unifié (Metadata + Liste des financements)
        const payload = {
            CodeCategorie: values.CODECATEGORIE || "",
            Libelle: values.LIBELLE,
            CodeFamille: values.FAMILLE,
            LibelleFamille: values.LIBELLEFAMILLE,
            EstLicite: true,
            ReglesUsage: values.REGLESUSAGE || "",
            EstActif: values.ESTACTIF || false,
            // On injecte les financements directement ici pour l'atomicité
            Financements: values.FINANCEMENT_ELIGIBLES || [] 
        };

        SweetAlert.fire({
            title: isAdd ? "Enregistrement" : "Modification",
            text: isAdd 
                ? "Voulez-vous créer cette catégorie avec ses éligibilités ?" 
                : "Voulez-vous modifier les informations et éligibilités de cette catégorie ?",
            showCancelButton: true,
            confirmButtonColor: "green",
            icon: "question"
        }).then((res) => {
            if (res.isConfirmed) {
                setLoading(true);
                let encryptedData = Crypter.encrypt(JSON.stringify(payload));
                
                const config = {
                    method: 'post', // On utilise POST pour l'action "Manage" (Upsert)
                    url: url,
                    data: encryptedData,
                    headers: { 
                        'Authorization': authHeader(), 
                        'Content-Type': 'application/json' 
                    }
                };

                axios(config).then((response) => {
                    const resJson = JSON.parse(Crypter.decrypt(response.data)).Value;
                    
                    if (resJson.StatutCode === 200) {
                        SweetAlert.fire("Succès", resJson.Message || "La catégorie a été enregistrée avec succès.", "success");
                        HandleAnnul(); // Réinitialisation complète
                    } else {
                        SweetAlert.fire({ 
                            title: resJson.Message || "Erreur métier", 
                            text: resJson.Result, 
                            icon: "warning" 
                        });
                    }
                    setLoading(false);
                }).catch((err) => {
                    setLoading(false);
                    console.error("Erreur Transaction:", err);
                    SweetAlert.fire("Erreur", "Le serveur a refusé la transaction.", "error");
                });
            }
        });
    };

    const formatMontant = (val: number) => new Intl.NumberFormat('fr-FR').format(val || 0);

    const columns = [
        {
            key: 'CODEACTIF', label: 'Code Actif', width: 100, align: 'center' as const,
            render: (row: any) => (
                <Tooltip placement="top" autoAdjustOverflow={false} title={row?.DESIGNATION || '-'}>
                    <span style={{ fontWeight: 'bold' }}>{row?.CODEACTIF || '-'}</span>
                </Tooltip>
            )
        },
        {
            key: 'CODECATEGORIE', label: 'Catégorie', width: 100, align: 'center' as const,
            render: (row: any) => (
                <Tooltip placement="top" autoAdjustOverflow={false} title={row?.LIBELLECATEGORIE || '-'}>
                    <span>{row?.CODECATEGORIE}</span>
                </Tooltip>
            )
        },
        {
            key: 'IDENTIFIANTUNIQUE', label: 'N° Série / Unique', width: 200,
            render: (row: any) => <span style={{ fontSize: '0.85rem' }}>{row?.IDENTIFIANTUNIQUE}</span>
        },
        {
            key: 'DESIGNATION', label: 'Désignation', width: 200,
            render: (row: any) => <div className="whitespace-normal break-words">{row?.DESIGNATION}</div>
        },
        {
            key: 'COUTACHAT', label: "Coût d'achat(XAF)", width: 140, align: 'right' as const,
            render: (row: any) => <span style={{ fontWeight: 'bold' }}>{formatMontant(row?.COUTINITIAL)}</span>
        },
        {
            key: 'VALEURRESIDUELLE', label: "V. Résiduelle", width: 120, align: 'right' as const,
            render: (row: any) => <span style={{ color: '#666' }}>{formatMontant(row?.VALEURRESIDUELLE)}</span>
        },
        {
            key: 'GRADEDESCRIPTION', label: 'Grade', width: 90, align: 'center' as const,
            render: (row: any) => <Tag color="blue">{row.GRADEDESCRIPTION}</Tag>
        },
        // {
        //     key: 'CODEFOURNISSEUR', label: 'Fournisseur', width: 100,
        //     render: (row: any) => <span>{row.lib_CODEFOURNISSEUR || row?.CODEFOURNISSEUR || 'N/A'}</span>
        // },
        {
    key: 'CODEFOURNISSEUR', label: 'Fournisseur', width: 100, align: 'center' as const,
    render: (row: any) => (
        <Tooltip placement="top" autoAdjustOverflow={false} title={row.LIBELLEFOURNISSEUR || row.lib_CODEFOURNISSEUR}>
            <span>{row.CODEFOURNISSEUR}</span>
        </Tooltip>
    )
},
        {
            key: 'STATUTVALIDATION', label: 'Statut', width: 110, align: 'center' as const,
            render: (row: any) => <Tag color="green">{row?.STATUTVALIDATION}</Tag>
        },
        {
            key: 'STATUT', label: 'État Opér.', width: 110, align: 'center' as const,
            render: (row: any) => <Tag color={row.STATUT === 'EN_FINANCEMENT' ? 'geekblue' : 'cyan'}>{row.STATUT?.replace('_', ' ')}</Tag>
        },
        {
            key: 'LOCALISATIONCOURANTE', label: 'Localisation', width: 230,
            render: (row: any) => <span>{row.LOCALISATIONCOURANTE}</span>
        },
        {
            key: 'DOCUMENTS', label: 'Documents', width: 100, align: 'center' as const,
            render: (row: any) => {
                const count = row.NBDOCUMENTS ?? row.NbDocuments ?? 0;
                return (
                    <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                        <IconButton size="small" onClick={() => openDocsModalWithLoad(row)} disabled={count === 0 && !row.CODEACTIF}>
                            <EyeOutlined style={{ fontSize: 18, color: count > 0 ? '#2A6596' : '#ccc' }} />
                        </IconButton>
                        <Tag color={count === 0 ? 'default' : 'green'}>{count}</Tag>
                    </Stack>
                );
            }
        },
        {
            key: 'ENREGISTREPAR', label: 'Saisi par', width: 110,
            render: (row: any) => (
                <Tooltip placement="top" autoAdjustOverflow={false} title={usersMap[row.ENREGISTREPAR] || row.ENREGISTREPAR || '-'}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '0.8rem' }}>{row.ENREGISTREPAR || '-'}</span>
                        <span style={{ fontSize: '0.65rem', color: 'gray' }}>
                            {row.DATEENREGISTREMENT ? dayjs(row.DATEENREGISTREMENT).format('DD/MM/YYYY HH:mm') : '-'}
                        </span>
                    </Box>
                </Tooltip>
            )
        },
        {
            key: 'VALIDEPAR', label: 'Validé par', width: 110,
            render: (row: any) => (
                <Tooltip placement="top" autoAdjustOverflow={false} title={usersMap[row.VALIDEPAR] || row.VALIDEPAR || '-'}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '0.8rem', color: '#107C41', fontWeight: 'bold' }}>{row.VALIDEPAR}</span>
                        <span style={{ fontSize: '0.65rem', color: 'gray' }}>
                            {row.DATEVALIDATION ? dayjs(row.DATEVALIDATION).format('DD/MM/YYYY HH:mm') : '-'}
                        </span>
                    </Box>
                </Tooltip>
            )
        }
    ];

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: <span><UserOutlined /> Catégorie & Actifs Validés</span>,
            children: <Tab1
                EtatG={EtatG}
                form={form}
                glActifsValides={glActifsValides}
                disabledForm={FormAct}
                listFinancements={listFinancements}
                columns={columns} />
        }
    ];

    return (
        <div className='tab'>
            <Typography align='center' className='formTitle'><b> GESTION DES CATÉGORIES D'ACTIFS </b></Typography>
            <Spin spinning={loading} size='large' style={{ color: 'green' }}>
                <Form form={form} initialValues={{ ESTLICITE: true }} onFinish={onFinish} preserve labelAlign="left">
                    <div className='bG'>
                        <legend><Typography color={"darkgreen"}>Actions</Typography></legend>
                        <Form.Item className='bouttong'>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button size='large' disabled={Consultactv} onClick={HandleConsult} block>Consulter</Button>
                                <Button size='large' disabled={Ajoutactv} onClick={HandleAjout} block>Ajouter</Button>
                                <Button size='large' disabled={Modifactv} onClick={HandleModif} block>Modifier</Button>
                                <Button size='large' disabled={Enrege} htmlType="submit" block>Enregistrer</Button>
                                <Button size='large' disabled={Annulactv} onClick={HandleAnnul} block>Annuler</Button>
                            </Space>
                        </Form.Item>
                    </div>
                    <div className='clientP'>
                        <Tabs size='large' type="card" defaultActiveKey="1" items={items} />
                    </div>
                </Form>
            </Spin>

            <AssetDocumentPreviewModal
                open={docModal.open}
                docs={docModal.docs}
                activeIndex={docModal.activeIndex}
                onSelect={(index) => setDocModal(prev => ({ ...prev, activeIndex: index }))}
                onBack={() => setDocModal(prev => ({ ...prev, activeIndex: null }))}
                onClose={closeDocsModal}
            />
        </div>
    );
};

export default CategorieActifPage;